import React, { useState } from 'react';
import {
  IAuditoriaMutacaoVolatilResultado,
  IAuditoriaMutacaoVolatilItem,
} from '../interfaces/IAuditoriaMutacaoVolatil';

interface Props {
  resultado: IAuditoriaMutacaoVolatilResultado;
}

const formatCurrency = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPct = (v: number) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const ItemRow: React.FC<{ item: IAuditoriaMutacaoVolatilItem }> = ({ item }) => {
  const [expandido, setExpandido] = useState(false);

  const isErro   = item.status === 'ERRO';
  const isAlerta = item.status === 'ALERTA';

  const rowBg   = isErro ? '#fef2f2' : isAlerta ? '#fffbeb' : '#f0fdf4';
  const corValor = isErro ? '#991b1b' : isAlerta ? '#92400e' : '#16a34a';

  return (
    <>
      <tr
        onClick={() => setExpandido(!expandido)}
        style={{ cursor: 'pointer', backgroundColor: rowBg }}
      >
        <td>{item.empresaNome}</td>
        <td>{item.mesReferencia}</td>
        <td>
          <span className={isErro ? 'badge bg-danger' : isAlerta ? 'badge bg-warning text-dark' : 'badge bg-success'}>
            {item.tipoClassificacao}
          </span>
        </td>
        <td className='text-end'>
          <span style={{ color: corValor, fontWeight: 600 }}>
            {formatCurrency(item.totalMes)}
          </span>
        </td>
        <td className='text-end'>{item.media3m > 0 ? formatCurrency(item.media3m) : '—'}</td>
        <td className='text-end'>
          {item.variacaoPercentual > 0 ? (
            <span style={{ color: corValor }}>{formatPct(item.variacaoPercentual)}</span>
          ) : '—'}
        </td>
        <td className='text-end'>{formatCurrency(item.saldoImobilizado)}</td>
        <td className='text-end'>
          <strong style={{ color: corValor }}>{formatCurrency(item.passivoTotal)}</strong>
        </td>
      </tr>

      {expandido && (
        <tr>
          <td colSpan={8} style={{ backgroundColor: '#f8fafc', padding: '1rem' }}>

            {/* Banner de diagnóstico */}
            <div
              className='alert mb-3 py-2'
              style={{
                backgroundColor: isErro ? '#fef2f2' : '#fef3c7',
                border: `1px solid ${isErro ? '#fca5a5' : '#fcd34d'}`,
              }}
            >
              <small>{item.mensagemDiagnostico}</small>
            </div>

            {/* Classificação */}
            <div className='mb-3'>
              <span className={`badge ${isErro ? 'bg-danger' : 'bg-warning text-dark'} me-2`}>
                {item.tipoClassificacao}
              </span>
              <small className='text-muted'>
                {isErro
                  ? `Lançamento individual de ${formatCurrency(item.valorLancamento)} representa ${formatPct(item.percentualImobilizado)} do saldo imobilizado (limite: 10%)`
                  : `Variação de ${formatPct(item.variacaoPercentual)} em relação à média dos 3 meses anteriores (limite: 30%)`}
              </small>
            </div>

            {/* Memória de cálculo */}
            <div className='mb-3'>
              <h6 className='text-muted mb-2'>Memória de Cálculo — Passivo Tributário Estimado</h6>
              <table className='table table-sm table-bordered mb-0' style={{ maxWidth: 500 }}>
                <tbody>
                  <tr>
                    <td>Valor Base</td>
                    <td className='text-end' style={{ color: corValor, fontWeight: 600 }}>
                      {isErro ? formatCurrency(item.valorLancamento) : formatCurrency(item.totalMes - item.media3m)}
                    </td>
                  </tr>
                  <tr>
                    <td>Tributo Principal (× 34% IRPJ 25% + CSLL 9%)</td>
                    <td className='text-end'>{formatCurrency(item.tributoPrincipal)}</td>
                  </tr>
                  <tr>
                    <td>Multa Qualificada (× 75% — lançamento de ofício)</td>
                    <td className='text-end'>{formatCurrency(item.multaQualificada)}</td>
                  </tr>
                  <tr>
                    <td>Juros SELIC (BCB Série 4189)</td>
                    <td className='text-end'>{formatCurrency(item.jurosSelic)}</td>
                  </tr>
                  <tr style={{ backgroundColor: isErro ? '#fef2f2' : '#fef3c7' }}>
                    <td><strong>Passivo Tributário Total</strong></td>
                    <td className='text-end'>
                      <strong style={{ color: corValor }}>{formatCurrency(item.passivoTotal)}</strong>
                    </td>
                  </tr>
                </tbody>
              </table>
              <small className='text-muted mt-1 d-block'>
                34% = IRPJ 25% (15% básico + 10% adicional) + CSLL 9% — alíquota combinada sobre a base ajustada.
              </small>
            </div>

            {/* Lançamentos do período */}
            {item.lancamentos.length > 0 && (
              <div>
                <h6 className='text-muted mb-2'>Lançamentos do Período</h6>
                <div className='table-responsive'>
                  <table className='table table-sm table-bordered mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th>Data</th>
                        <th>Histórico</th>
                        <th>Tipo</th>
                        <th>Conta Contábil</th>
                        <th>Descrição</th>
                        <th className='text-end'>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.lancamentos.map((lanc, idx) => (
                        <tr key={idx}>
                          <td>{lanc.dataLancamento}</td>
                          <td>{lanc.historico}</td>
                          <td>
                            <span className={`badge ${lanc.tipoLancamento === 'D' ? 'bg-primary' : 'bg-secondary'}`}>
                              {lanc.tipoLancamento}
                            </span>
                          </td>
                          <td>{lanc.codigoContabil}</td>
                          <td>{lanc.descricaoConta}</td>
                          <td className='text-end'>{formatCurrency(lanc.valor)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaMutacaoVolatilDetalhe: React.FC<Props> = ({ resultado }) => {
  const temErro   = resultado.totalErros > 0;
  const temAlerta = resultado.totalAlertas > 0;

  return (
    <div className='auditoria-card'>
      <h4>Mutação Volátil — Imobilizado vs Despesas de Manutenção</h4>
      <p className='text-muted'>
        Detecta movimentação volátil entre Ativo Imobilizado (1.02.01.01) e Despesas com Manutenção (3.01.01.06),
        sinalizando possível manipulação de EBITDA ou dedutibilidade fiscal indevida de gastos de capital
        reclassificados como despesa operacional (CPC 27 / RIR/2018, Art. 351).
      </p>

      {temErro && (
        <div className='alert alert-danger mb-4'>
          <strong>ERRO DE AUDITORIA: Lançamento Desproporcional ao Ativo Imobilizado</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}

      {!temErro && temAlerta && (
        <div className='alert alert-warning mb-4'>
          <strong>ALERTA DE AUDITORIA: Volatilidade em Despesas de Manutenção</strong>
          <br />
          <small>{resultado.mensagemDirecionamento}</small>
        </div>
      )}

      {/* KPIs */}
      <div className='auditoria-kpis mb-4' style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className={`auditoria-kpi ${temErro ? 'auditoria-kpi-critico' : temAlerta ? 'auditoria-kpi-alerta' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Exposição Total</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.exposicaoTotalGeral)}</div>
          <div className='auditoria-kpi-help'>Passivo Tributário + Multa + Juros</div>
        </div>
        <div className={`auditoria-kpi ${temErro ? 'auditoria-kpi-critico' : temAlerta ? 'auditoria-kpi-alerta' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Passivo Tributário (34%)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.passivoTributarioTotal)}</div>
          <div className='auditoria-kpi-help'>IRPJ 25% + CSLL 9% sobre base ajustada</div>
        </div>
        <div className={`auditoria-kpi ${temErro || temAlerta ? 'auditoria-kpi-alerta' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Multa + Juros SELIC</div>
          <div className='auditoria-kpi-value'>
            {formatCurrency(resultado.multaQualificadaTotal + resultado.jurosSelicTotal)}
          </div>
          <div className='auditoria-kpi-help'>Multa 75% + Juros BCB Série 4189</div>
        </div>
        <div className='auditoria-kpi auditoria-kpi-disabled'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value' style={{ fontSize: '0.7rem', lineHeight: 1.4 }}>
            CPC 27 · RIR/2018 Art.351 · SPED
          </div>
        </div>
      </div>

      {/* Tabela */}
      {resultado.itens.length === 0 ? (
        <div className='auditoria-empty-state'>
          <p>
            Nenhuma volatilidade relevante ou lançamento desproporcional identificado entre
            Ativo Imobilizado (1.02.01.01) e Despesas de Manutenção (3.01.01.06) no período selecionado.
          </p>
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table table-hover align-middle'>
            <thead className='table-light'>
              <tr>
                <th>Empresa</th>
                <th>Mês</th>
                <th>Classificação</th>
                <th className='text-end'>Total do Mês</th>
                <th className='text-end'>Média 3m</th>
                <th className='text-end'>Variação</th>
                <th className='text-end'>Saldo Imobilizado</th>
                <th className='text-end'>Passivo Total</th>
              </tr>
            </thead>
            <tbody>
              {resultado.itens.map((item, idx) => (
                <ItemRow key={`${item.empresaId}-${item.mesReferencia}-${idx}`} item={item} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fundamentação legal */}
      {resultado.fundamentacaoLegal && (
        <details className='mt-4'>
          <summary className='text-muted' style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
            Fundamentação Legal
          </summary>
          <div className='mt-2 p-3' style={{ backgroundColor: '#f8fafc', fontSize: '0.8rem', lineHeight: 1.6 }}>
            {resultado.fundamentacaoLegal}
          </div>
        </details>
      )}
    </div>
  );
};

export default AuditoriaMutacaoVolatilDetalhe;
